import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  InputLabel,
  Typography,
  Divider,
} from "@material-ui/core";
import { Formik } from "formik";
import moment from "moment";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { fetchWrapper } from "src/services/http_requests";
import theme from "src/theme";
import { loadData } from "src/utils/sessionStorage";
import CommonTextField from "src/components/CommonComponents/CommonTextField";
import * as Yup from "yup";
import QueryBuilderOutlinedIcon from '@material-ui/icons/QueryBuilderOutlined';
import TrendingUpOutlinedIcon from '@material-ui/icons/TrendingUpOutlined';
import personInfoIcon from 'src/assets/images/person-info.svg';
import insuranceIcon from 'src/assets/images/insurance-shield.svg';
import loanIcon from 'src/assets/images/loan-finance.svg';
import savingsIcon from 'src/assets/images/saving-piggy.svg';

const BASE_URL = process.env.REACT_APP_API_URL;

const CommonViewSection = ({
  title,
  typeLabel,
  typeValue,
  answerLabel,
  answerValue,
  icon = null,
  imagePath = null,
}) => {
  let isNA = false;
  const renderTypeValue = (value) => {
    if (!value ) {
      return <Typography variant="body2">-</Typography>;
    }

    // Handle different input formats (comma-separated, space-separated, array)
    let items = [];
    if (Array.isArray(value)) {
      if (value.length === 0 || (value.length === 1 && typeof value[0] === 'number')) {
        isNA = true;
        return <Typography variant="body2">NA</Typography>;
      }
      items = value;
    } else if (typeof value === 'string') {
      // Split by comma, semicolon, or multiple spaces
      items = value
        .split(/[,;]|\s{2,}/)
        .map(item => item.trim())
        .filter(item => item.length > 0);
      
      // If no separators found, treat as single item
      if (items.length <= 1) {
        items = [value.trim()];
      }
    }
    
    return items.map((item, index) => (
      <Typography 
        key={index} 
        variant="body2" 
        style={{ marginBottom: index < items.length - 1 ? 4 : 0 }}
      >
        • {item}
      </Typography>
    ));
  };

  return (
    <>
      {/* Section Header */}
      <Box display="flex" flexDirection="column" width="100%">
        <Typography variant="h6" color="primary" gutterBottom style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 8 }}>
          {icon ? icon : <span className="w-5 h-[21px]">
              <img src={imagePath} alt={imagePath} className="w-5 h-[20px]" />
            </span>}
           {title}
        </Typography>
        <Divider />
      </Box>
      
      {/* Type/Option Field */}
      <Box width="100%" className="mb-4 ml-7">
        <InputLabel 
          style={{ 
            fontSize: 15, 
            fontWeight: 500, 
            color: theme.palette.text.primary, 
            marginBottom: 8 
          }}
        >
          {typeLabel}
        </InputLabel>
        <Box 
          style={{ 
            padding: '12px',
            backgroundColor: '#f5f5f5', 
            borderRadius: 4, 
            minHeight: 40, 
            marginLeft: 16,
            marginRight: 20
          }}
        >
          {renderTypeValue(typeValue)}
        </Box>
      </Box>
      
      {/* Answer Field */}
      {!isNA && <Box width="100%" className="ml-7">
        <InputLabel 
          style={{ 
            fontSize: 15, 
            fontWeight: 500, 
            color: theme.palette.text.primary, 
            marginBottom: 8 
          }}
        >
          {answerLabel}
        </InputLabel>
        <Typography 
          variant="body2" 
          style={{ 
            padding: '12px', 
            backgroundColor: '#f5f5f5', 
            borderRadius: 4, 
            minHeight: 40,
            marginLeft: 16,
            marginRight: 20
          }}
        >
          {answerValue || '-'}
        </Typography>
      </Box>}
    </>
  );
};

function ResilienceViewForm({ resilienceDetails, crudAction, refetchUserList }) {
  const { role_id, name } = loadData('loginInfo')?.data;
  const navigate = useNavigate();
  
  const [resilienceViewData, setResilienceViewData] = useState(updateResilienceViewData(resilienceDetails));
  
  const [action, setAction] = useState({
    approve: false,
    reject: false,
  });
  
  const [open, setOpen] = React.useState(false);
  const [openConfirmation, setOpenConfirmation] = useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };
  
  const handleClickOpenConfirm = () => {
    setOpenConfirmation(true);
  };

  const handleClose = () => {
    setOpen(false);
  };
  
  const handleCloseConfirm = () => {
    setOpenConfirmation(false);
  };
  
  const putApproveAndRejectResilience = async (body) => {
    try {
      const data = await fetchWrapper.put(
        `${BASE_URL}/resilience/isApproved/${resilienceDetails?.id}`,
        body
      );
      if (data.success) {
        toast.success(data?.message);
        setOpenConfirmation(false);
        if (data?.success) {
          setTimeout(() => {
            refetchUserList()
          }, 1000);
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  function updateResilienceViewData(resilienceDetails) {
    return {
      beneficiary_name: resilienceDetails?.beneficiary?.name || "",
      beneficiary_id: resilienceDetails?.beneficiary?.id || "",
      fathers_husbands_name: resilienceDetails?.beneficiary?.father_name || "",
      mobile_number: resilienceDetails?.beneficiary?.mobile || "",
      mapping_date: resilienceDetails?.created_at || "",
      hh_id: resilienceDetails?.household_id || "",
      resilience_score: resilienceDetails?.score || "",
      
      // Savings
      savings_type: resilienceDetails?.savings?.option || "",
      savings_last_transaction: resilienceDetails?.savings?.answer || "",
      
      // Investment
      investment_type: resilienceDetails?.investment?.option || "",
      investment_last_contribution: resilienceDetails?.investment?.answer || "",
      
      // Insurance
      insurance_type: resilienceDetails?.insurance?.option || "",
      insurance_last_premium: resilienceDetails?.insurance?.answer || "",
      
      // Loan
      loan_type: resilienceDetails?.loan?.option || "",
      loan_last_emi: resilienceDetails?.loan?.answer || "",
      
      // Pension
      pension_type: resilienceDetails?.pension?.option || "",
      pension_last_contribution: resilienceDetails?.pension?.answer || ""
    }
  }

  useEffect(() => {
    setResilienceViewData(updateResilienceViewData(resilienceDetails));
  }, [resilienceDetails]);

  const checkIsActionBtnVisible = () => {
    if (role_id === 2 && Number(resilienceDetails?.is_approve_status_fo) === 2) {
      return [null, 0].includes(resilienceDetails?.ngo_action_count)
    } else return false;
  };

  return (
    <Box className="w-full h-full flex flex-col justify-start items-start py-8">
      <Formik
        enableReinitialize={crudAction.create ? false : true}
        initialValues={{
          beneficiary_name: resilienceViewData?.beneficiary_name || "",
          beneficiary_id: resilienceViewData?.beneficiary_id || "",
          fathers_husbands_name: resilienceViewData?.fathers_husbands_name || "",
          mobile_number: resilienceViewData?.mobile_number || "",
          mapping_date: moment(resilienceViewData?.mapping_date).format("DD/MM/YYYY") || "",
          hh_id: resilienceViewData?.hh_id || "",
          resilience_score: resilienceViewData?.resilience_score || "",
          
          // Savings
          savings_type: resilienceViewData?.savings_type || "",
          savings_last_transaction: resilienceViewData?.savings_last_transaction || "",
          
          // Investment
          investment_type: resilienceViewData?.investment_type || "",
          investment_last_contribution: resilienceViewData?.investment_last_contribution || "",
          
          // Insurance
          insurance_type: resilienceViewData?.insurance_type || "",
          insurance_last_premium: resilienceViewData?.insurance_last_premium || "",
          
          // Loan
          loan_type: resilienceViewData?.loan_type || "",
          loan_last_emi: resilienceViewData?.loan_last_emi || "",
          
          // Pension
          pension_type: resilienceViewData?.pension_type || "",
          pension_last_contribution: resilienceViewData?.pension_last_contribution || "",
          
          comments_by_FO: resilienceViewData?.comments_by_FO || "",
          concluding_remarks_by_NGO: resilienceViewData?.concluding_remarks_by_NGO || "",
        }}
        validationSchema={Yup.object().shape({
          concluding_remarks_by_NGO: Yup.string().required("Required Field")
        })}
        onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
          handleClickOpenConfirm();
        }}
      >
        {function Render({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          setFieldValue,
          setFieldTouched,
          touched,
          setErrors,
          setStatus,
          setSubmitting,
          values,
          setFieldError,
          resetForm
        }) {
          useEffect(() => {
            return () => resetForm();
          }, []);
          
          return (
            <form className="w-full bg-white overflow-hidden" onSubmit={handleSubmit} style={{marginTop: -20}}>
              <Dialog style={{ maxWidth: "100%" }} onClose={handleClose} open={open}>
                <DialogContent>
                  <DialogContentText>
                    Resilience Details
                  </DialogContentText>
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleClose}>Close</Button>
                </DialogActions>
              </Dialog>

              <Grid container spacing={2} className="w-full bg-white px-4">
                {/* Basic Information */}
                <Grid item xs={12}>
                  <Typography variant="h6" color="primary" gutterBottom style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span className="w-5 h-[20px]"><img src={personInfoIcon} alt="person-info" className="w-5 h-5" /></span> Basic Information
                  </Typography>
                  <Divider />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <CommonTextField
                    name="beneficiary_name"
                    label="Beneficiary Name"
                    value={values.beneficiary_name}
                    error={errors.beneficiary_name}
                    helperText={errors.beneficiary_name}
                    touched={touched}
                    handleBlur={handleBlur}
                    handleChange={handleChange}
                    crudAction={crudAction}
                    disabledTextColor={theme.palette.text.primary}
                    disabledBorderColor={theme.palette.text.primary}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <CommonTextField
                    name="beneficiary_id"
                    label="Beneficiary ID"
                    value={values.beneficiary_id}
                    error={errors.beneficiary_id}
                    helperText={errors.beneficiary_id}
                    touched={touched}
                    handleBlur={handleBlur}
                    handleChange={handleChange}
                    crudAction={crudAction}
                    disabledTextColor={theme.palette.text.primary}
                    disabledBorderColor={theme.palette.text.primary}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <CommonTextField
                    name="fathers_husbands_name"
                    label="Father's/Husband's Name"
                    value={values.fathers_husbands_name}
                    error={errors.fathers_husbands_name}
                    helperText={errors.fathers_husbands_name}
                    touched={touched}
                    handleBlur={handleBlur}
                    handleChange={handleChange}
                    crudAction={crudAction}
                    disabledTextColor={theme.palette.text.primary}
                    disabledBorderColor={theme.palette.text.primary}
                    InputLabelProps={{
                      style: {
                        backgroundColor: 'white',
                        paddingRight: "2px",
                        fontSize: 14,
                        marginLeft: 0
                      }
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <CommonTextField
                    name="mobile_number"
                    label="Mobile Number"
                    value={values.mobile_number}
                    error={errors.mobile_number}
                    helperText={errors.mobile_number}
                    touched={touched}
                    handleBlur={handleBlur}
                    handleChange={handleChange}
                    crudAction={crudAction}
                    disabledTextColor={theme.palette.text.primary}
                    disabledBorderColor={theme.palette.text.primary}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <CommonTextField
                    name="mapping_date"
                    label="Mapping Date"
                    value={values.mapping_date}
                    error={errors.mapping_date}
                    helperText={errors.mapping_date}
                    touched={touched}
                    handleBlur={handleBlur}
                    handleChange={handleChange}
                    crudAction={crudAction}
                    disabledTextColor={theme.palette.text.primary}
                    disabledBorderColor={theme.palette.text.primary}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <CommonTextField
                    name="hh_id"
                    label="HH ID"
                    value={values.hh_id}
                    error={errors.hh_id}
                    helperText={errors.hh_id}
                    touched={touched}
                    handleBlur={handleBlur}
                    handleChange={handleChange}
                    crudAction={crudAction}
                    disabledTextColor={theme.palette.text.primary}
                    disabledBorderColor={theme.palette.text.primary}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <CommonTextField
                    name="resilience_score"
                    label="Resilience Score"
                    value={values.resilience_score || "0"}
                    error={errors.resilience_score}
                    helperText={errors.resilience_score}
                    touched={touched}
                    handleBlur={handleBlur}
                    handleChange={handleChange}
                    crudAction={crudAction}
                    disabledTextColor={theme.palette.text.primary}
                    disabledBorderColor={theme.palette.text.primary}
                  />
                </Grid>

                {/* Savings Section */}
                <Grid item xs={12} style={{padding: "0px 8px"}}>
                  <CommonViewSection
                    title="Savings"
                    typeLabel="Type of savings product"
                    typeValue={values.savings_type}
                    answerLabel="When did you last make a transaction?"
                    answerValue={values.savings_last_transaction}
                    imagePath={savingsIcon}
                  />
                </Grid>

                {/* Investment Section */}
                <Grid item xs={12} style={{padding: "0px 8px"}}>
                  <CommonViewSection
                    title="Investment"
                    typeLabel="Type of investment product"
                    typeValue={values.investment_type}
                    answerLabel="When did you last pay the investment contribution?"
                    answerValue={values.investment_last_contribution}
                    icon={<TrendingUpOutlinedIcon style={{fontSize: 20}}/>}
                  />
                </Grid>

                {/* Insurance Section */}
                <Grid item xs={12} style={{padding: "0px 8px"}}>
                  <CommonViewSection
                    title="Insurance"
                    typeLabel="Type of insurance"
                    typeValue={values.insurance_type}
                    answerLabel="When did you pay your last premium?"
                    answerValue={values.insurance_last_premium}
                    imagePath={insuranceIcon}
                  />
                </Grid>

                {/* Loan Section */}
                <Grid item xs={12} style={{padding: "0px 8px"}}>
                  <CommonViewSection
                    title="Loan"
                    typeLabel="Type/Source of loan"
                    typeValue={values.loan_type}
                    answerLabel="When did you pay the last EMI?"
                    answerValue={values.loan_last_emi}
                    imagePath={loanIcon}
                    />
                </Grid>

                {/* Pension Section */}
                <Grid item xs={12} style={{padding: "0px 8px"}}>
                  <CommonViewSection
                    title="Pension"
                    typeLabel="Type of pension"
                    typeValue={values.pension_type}
                    answerLabel="When did you pay your last premium/contribution?"
                    answerValue={values.pension_last_contribution}
                    icon={<QueryBuilderOutlinedIcon style={{fontSize: 20}}/>}
                  />
                </Grid>

                {/* Action Buttons */}
                <Grid item xs={12} md={6}>
                  {checkIsActionBtnVisible() && (
                    <Box className="w-full my-4 py-8 flex justify-start items-center space-x-3">
                      <Button
                        className="w-6/12 md:2/12"
                        variant="contained"
                        onClick={() => {
                          setFieldError("concluding_remarks_by_NGO", "");
                          setAction({
                            approve: true,
                            reject: false,
                          });
                          handleClickOpenConfirm();
                        }}
                        style={{
                          backgroundColor: theme.palette.success.main,
                          color: "white",
                        }}
                        disabled={resilienceDetails?.ngo_action_count === 0 && resilienceDetails.is_approve_status_ngo === 2}
                      >
                        APPROVE
                      </Button>
                      <Button
                        className="w-6/12 md:2/12"
                        variant="contained"
                        color="primary"
                        onClick={() => {
                          setFieldTouched("concluding_remarks_by_NGO", true);
                          setAction({
                            approve: false,
                            reject: true,
                          });
                        }}
                        type="submit"
                        disabled={resilienceDetails?.ngo_action_count === 0 && resilienceDetails.is_approve_status_ngo === 3}
                      >
                        REJECT
                      </Button>
                    </Box>
                  )}
                </Grid>
              </Grid>
              
              <Dialog
                open={openConfirmation}
                onClose={handleCloseConfirm}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
              >
                <DialogTitle id="alert-dialog-title">
                  {"Confirmation Alert"}
                </DialogTitle>
                <DialogContent>
                  <DialogContentText id="alert-dialog-description">
                    Are you sure you want to {action?.approve ? "approve" : "reject"} this household resilience mapping?
                  </DialogContentText>
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleCloseConfirm}>Disagree</Button>
                  <Button onClick={() => {
                    let statusObj = {
                      is_approved_status: action.approve ? 2 : action.reject ? 3 : '',
                      is_approved_date: moment(),
                      is_rejected_reason: values?.concluding_remarks_by_NGO,
                      ngo_action_count: resilienceDetails?.ngo_action_count == null ? 0 : 1,
                    }
                    putApproveAndRejectResilience(statusObj);
                  }} autoFocus>
                    Agree
                  </Button>
                </DialogActions>
              </Dialog>
            </form>
          );
        }}
      </Formik>
    </Box>
  );
}

export default React.memo(ResilienceViewForm); 